import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    DatetimePicker,
    DynamicInput,
    FormLabel,
    IValidateError,
    OptionType,
    SearchableSelect,
} from "shared";
import {
    IBankDetail,
    ILegalBankDetail,
    ITemplateYandexComponent,
    YandexListTypes
} from "entities/template";
import { legalOrgsService } from "entities/legal_orgs";
import type { ILegalOrg, ILegalOrgBankDetail, ILegalOrgListItem } from "entities/legal_orgs";
import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { YandexDocumentSelectField } from "../../../../lib/const/yandex_document_select_fields";
import { LegalYandexWordFields } from "../../../../lib/const/legal_yandex_word_fields";
import {
    fullYandexLegalFields,
    legalYandexBankFields,
    legalYandexOrganizationFields
} from "../../../../lib/utils/field_list";
import {
    constructAccountCurrencyOptions,
    constructOptions,
    findPriorityYandexOrgYaId,
    sortAccountTypeLabelsForSelect,
    sortLegalOrgSelectOptionsFirstByYaId,
} from "../../../../lib/utils/dynamic_document_form_utils";
import { getCountryRuNameByIsoCode } from "../../../../lib/utils/countries_ru_names";
import { ExternalUpdateMode } from "../../../../lib/types/share-state";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import { cleanUndefinedNullObject } from "../../../../lib/utils/cleanupExternalRecord";
import styles from "./document_form_legal_yandex.module.scss";
import { legalOrgsFormContext } from "../../../../lib/providers/legal-orgs-form-provider";
import {
    collectSelectableOrgsForLegalYandexDocument,
    filterBankDetailsByListFilter,
} from "../../../../lib/utils/legal_orgs_scope_selection";

const DEFAULT_OPTIONS_LIST = YandexListTypes.yaOrgs;

const LEGAL_YANDEX_ENG_NAME_SHORT_FIELD = LegalYandexWordFields.ya_eng_name_short;

const LEGAL_YANDEX_ACCOUNT_TYPE_FIELD = LegalYandexWordFields.ya_account_type;
const LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD = LegalYandexWordFields.ya_account_currency;

/** Поля банка, которые в форме — селекты; остальные из `legalYandexBankFields` — текст/инпуты. */
function isLegalYandexSelectLikeBankField(key: string): boolean {
    return (
        key === YandexDocumentSelectField.ya_bank_name
        || key === YandexDocumentSelectField.ya_bic
        || key === YandexDocumentSelectField.ya_payment_account
        || key === YandexDocumentSelectField.ya_corr_account
        || key === LEGAL_YANDEX_ACCOUNT_TYPE_FIELD
        || key === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD
    );
}

/** Подбор опции селекта компании из predefined (value может быть ya_id или подпись). */
function resolveLegalYandexOrgSelectOption(
    strVal: string | undefined,
    options: OptionType[] | undefined,
): OptionType | undefined {
    if (strVal === undefined || strVal === "") {
        return undefined;
    }
    if (!options?.length) {
        return { value: strVal, label: strVal };
    }
    const found = options.find(
        (o) =>
            String(o.value) === strVal
            || String(o.label) === strVal
            || o.label === strVal
    );
    return found ?? { value: strVal, label: strVal };
}

/** Есть ли в external/URL ключи идентичности организации (даже если резолв в ya_id ещё не удался). */
function hasExternalOrgIdentityKeys(ext: Record<string, any>, suffix: string): boolean {
    const keys = [
        `${LegalYandexWordFields.ya_id}${suffix}`,
        `${YandexDocumentSelectField.ya_full_name}${suffix}`,
        `${YandexDocumentSelectField.ya_name}${suffix}`,
        `${LEGAL_YANDEX_ENG_NAME_SHORT_FIELD}${suffix}`,
    ];
    return keys.some((key) => {
        const v = ext[key];
        return v != null && String(v) !== "";
    });
}

/** Резолв ya_id из external/URL: ya_id → ya_full_name → ya_name. */
function resolveOrgYaIdFromExternal(
    ext: Record<string, any>,
    suffix: string,
    orgs: ILegalOrgListItem[],
): string | undefined {
    const byId = ext[`${LegalYandexWordFields.ya_id}${suffix}`];
    if (byId != null && String(byId) !== "") {
        return String(byId);
    }

    const fullName = ext[`${YandexDocumentSelectField.ya_full_name}${suffix}`];
    if (fullName != null && String(fullName) !== "") {
        const org = orgs.find((o) => String(o.ya_full_name) === String(fullName));
        if (org?.ya_id != null && String(org.ya_id) !== "") {
            return String(org.ya_id);
        }
    }

    const name = ext[`${YandexDocumentSelectField.ya_name}${suffix}`];
    if (name != null && String(name) !== "") {
        const org = orgs.find((o) => String(o.ya_name) === String(name));
        if (org?.ya_id != null && String(org.ya_id) !== "") {
            return String(org.ya_id);
        }
    }

    return undefined;
}

function bankDetailRowFingerprint(detail: ILegalOrgBankDetail): string {
    return [
        detail.id ?? "",
        detail.ya_bic ?? "",
        detail.ya_payment_account ?? "",
        detail.ya_bank_name ?? "",
        detail.ya_corr_account ?? "",
    ].join("|");
}

/** Убирает из снимка predefined только текстовые банковские ключи (не селекты), чтобы не подтягивать старый текст после смены строки банка. */
function stripNonSelectBankTextKeysFromPendingExternal(
    pendingRef: React.MutableRefObject<Record<string, any> | null>,
    suffix: string,
): void {
    const snap = pendingRef.current;
    if (!snap) {
        return;
    }
    const next = { ...snap };
    let changed = false;
    for (const key of legalYandexBankFields) {
        if (isLegalYandexSelectLikeBankField(key)) {
            continue;
        }
        const extKey = `${key}${suffix}`;
        if (Object.prototype.hasOwnProperty.call(next, extKey)) {
            delete next[extKey];
            changed = true;
        }
    }
    if (changed) {
        pendingRef.current = Object.keys(next).length > 0 ? next : null;
    }
}

function uniqueDefinedStrings(values: (string | undefined)[]): string[] {
    return Array.from(
        new Set(values.filter((v): v is string => v != null && String(v) !== ""))
    );
}

function normalizeYaIdFromOption(opt?: OptionType): string | null {
    if (opt?.value == null || String(opt.value) === "") {
        return null;
    }
    return String(opt.value);
}

function optionValueAsString(opt?: OptionType): string {
    if (opt?.value == null || String(opt.value) === "") {
        return "";
    }
    return String(opt.value);
}

function isInnOrRegNumLegalYandexField(field: string): boolean {
    return field === LegalYandexWordFields.ya_inn || field === LegalYandexWordFields.ya_reg_num;
}

interface IDocumentFormLegalYandexProps {
    component: ITemplateYandexComponent;
    externalUpdateRecord: Record<string, any>;
    onChange: (partial: Record<string, any>) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    isExternalUser: boolean;
    isExternalProtected: boolean;
    triggerValidate: number;
    externalUpdateMode: ExternalUpdateMode;
}

export const DocumentFormLegalYandex: React.FC<IDocumentFormLegalYandexProps> = React.memo(({
                                                                                                component,
                                                                                                label,
                                                                                                triggerValidate,
                                                                                                tooltip,
                                                                                                readonly,
                                                                                                isExternalUser,
                                                                                                isExternalProtected,
                                                                                                externalUpdateRecord,
                                                                                                externalUpdateMode,
                                                                                                onValidationRegister,
                                                                                                onChange,
                                                                                            }) => {
    const [inn, setInn] = useState<string>("");
    const [regNum, setRegNum] = useState<string>("");
    const [selectedOrgYaId, setSelectedOrgYaId] = useState<string | null>();
    const [record, setRecord] = useState<Record<string, string | undefined>>({});
    const [bankDetails, setBankDetails] = useState<ILegalOrgBankDetail[]>([]);
    const bankDetailsRef = useRef<ILegalOrgBankDetail[]>([]);
    const [companyName, setCompanyName] = useState<OptionType>();
    const [companyNameOptions, setCompanyNameOptions] = useState<OptionType[]>([]);
    const [bankName, setBankName] = useState<OptionType>();
    const [bic, setBic] = useState<OptionType>();
    const [accountType, setAccountType] = useState<OptionType>();
    const [accountCurrency, setAccountCurrency] = useState<OptionType>();
    const [paymentAccount, setPaymentAccount] = useState<OptionType>();
    const [paymentAccountOptions, setPaymentAccountOptions] = useState<OptionType[]>([]);
    const [corrAccountOptions, setCorrAccountOptions] = useState<OptionType[]>([]);
    const [corrAccount, setCorrAccount] = useState<OptionType>();
    const [companyEngNameShortOptions, setCompanyEngNameShortOptions] = useState<OptionType[]>([]);
    const [companyEngNameShort, setCompanyEngNameShort] = useState<OptionType>();
    const { orgs, loading: legalOrgsLoading, extUserId: legalOrgsExtUserId } = useContext(legalOrgsFormContext);
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const orgFetchGenerationRef = useRef(0);
    const lastLoadedOrgYaIdRef = useRef<string | null>(null);
    const pendingFetchYaIdRef = useRef<string | null>(null);
    const selectedOrgYaIdRef = useRef<string | null>(null);
    const selectableOrgsRef = useRef<ILegalOrgListItem[]>([]);
    const componentRef = useRef(component);
    const externalUpdateRecordRef = useRef(externalUpdateRecord);
    const externalUpdateModeRef = useRef(externalUpdateMode);
    /** Накопленный predefined/external по банковским ключам; при ручной очистке селектов банковские ключи вырезаются, чтобы reapply не вернул значения. */
    const pendingExternalBankApplyRef = useRef<Record<string, any> | null>(null);
    /** То же по `externalUpdateRecord`, что и для банка, но только сценарий consume для полей организации (отдельный снимок). */
    const pendingExternalOrgApplyRef = useRef<Record<string, any> | null>(null);
    const prevAccountTypeValueRef = useRef<string>("");
    const prevAccountCurrencyValueRef = useRef<string>("");
    const prevBankNameValueRef = useRef<string>("");
    const prevBicValueRef = useRef<string>("");
    const prevPaymentAccountValueRef = useRef<string>("");
    const prevCorrAccountValueRef = useRef<string>("");
    /** После сброса валюты/типа эффект по paymentAccount в том же тике ещё видит старый bic/счёт и иначе вернул бы валюту/тип из detail. */
    const suppressPaymentRowCurrencyTypeSyncRef = useRef(false);
    /** Какое поле пользователь менял последним: банк → пересчитываем БИК; БИК → пересчитываем банк (иначе второй проход эффекта после setBic выглядит как смена БИК). */
    const bankChainUiLeadRef = useRef<"bank" | "bic" | null>(null);
    /** Один кадр не синхронизировать цепочку после программного сброса (state в замыкании ещё старый). */
    const suppressBankChainReconcileRef = useRef(false);
    const clearBankActionTriggered = useRef(false);
    /** Запрет повторного автозаполнения после очистки (ветка с bankDetails.length === 1). Автозаполнение смотрит на bankDetailScope. Сброс при смене компании. */
    const suppressSingleRowBankAutoFillRef = useRef(false);
    /** Последняя применённая строка банка (счёт+бик+…); при смене строки вычищаем текстовые ключи из predefined-снимка. */
    const lastAppliedBankDetailFingerprintRef = useRef<string>("");

    const componentSuffix = useMemo(() => {
        return component.suffix ? component.suffix : "";
    }, [component.suffix]);

    const selectFields = useMemo(
        () =>
            [
                ...Object.values(YandexDocumentSelectField).filter(
                    (f) => f !== YandexDocumentSelectField.ya_full_name
                ),
                LEGAL_YANDEX_ENG_NAME_SHORT_FIELD,
                LEGAL_YANDEX_ACCOUNT_TYPE_FIELD,
                LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD,
            ] as string[],
        []
    );

    const selectFieldsRef = useRef(selectFields);
    selectFieldsRef.current = selectFields;

    const hasAccountTypeFieldOnForm = useMemo(
        () => !!(component.fieldsList?.includes(LEGAL_YANDEX_ACCOUNT_TYPE_FIELD)),
        [component.fieldsList]
    );

    const hasAccountCurrencyFieldOnForm = useMemo(
        () => !!(component.fieldsList?.includes(LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD)),
        [component.fieldsList]
    );

    const legalYandexBankFieldsForForm = useMemo(
        () =>
            legalYandexBankFields.filter((f) => {
                if (f === "ya_account_type" && !hasAccountTypeFieldOnForm) {
                    return false;
                }
                if (f === "ya_account_currency" && !hasAccountCurrencyFieldOnForm) {
                    return false;
                }
                return true;
            }),
        [hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]
    );

    const accountCurrencyOptions = useMemo(() => {
        if (!hasAccountCurrencyFieldOnForm) {
            return [] as OptionType[];
        }
        return constructAccountCurrencyOptions(
            uniqueDefinedStrings(bankDetails.map((d) => d.ya_account_currency))
        );
    }, [bankDetails, hasAccountCurrencyFieldOnForm]);

    const currencyKey =
        accountCurrency?.value != null && String(accountCurrency.value) !== ""
            ? String(accountCurrency.value)
            : "";

    const typeKey =
        accountType?.value != null && String(accountType.value) !== ""
            ? String(accountType.value)
            : "";

    const accountTypeOptions = useMemo(() => {
        if (!hasAccountTypeFieldOnForm) {
            return [] as OptionType[];
        }
        let src = bankDetails;
        if (hasAccountCurrencyFieldOnForm && currencyKey) {
             src = bankDetails.filter((d) => d.ya_account_currency === currencyKey);
        }
        const types = uniqueDefinedStrings(src.map((d) => d.ya_account_type));
        return sortAccountTypeLabelsForSelect(types).map((o) => ({ value: o, label: o }));
    }, [bankDetails, currencyKey, hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]);

    /** Фильтр по валюте/типу только если соответствующие поля есть в fieldsList. */
    const bankDetailScope = useMemo(() => {
        let scoped = bankDetails;
        if (hasAccountCurrencyFieldOnForm && currencyKey) {
            scoped = scoped.filter((d) => d.ya_account_currency === currencyKey);
        }
        if (hasAccountTypeFieldOnForm && typeKey) {
            scoped = scoped.filter((d) => d.ya_account_type === typeKey);
        }
        return scoped;
    }, [bankDetails, currencyKey, typeKey, hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]);

    const bankNameOptionsFromDetails = useMemo(
        () =>
            constructOptions(uniqueDefinedStrings(bankDetailScope.map((d) => d.ya_bank_name))),
        [bankDetailScope]
    );

    const bicOptionsFromDetails = useMemo(
        () => constructOptions(uniqueDefinedStrings(bankDetailScope.map((d) => d.ya_bic))),
        [bankDetailScope]
    );

    const selectVariablesMapping: Record<string, any> = {
        [YandexDocumentSelectField.ya_name]: [companyName, setCompanyName, companyNameOptions],
        [LEGAL_YANDEX_ENG_NAME_SHORT_FIELD]: [
            companyEngNameShort,
            setCompanyEngNameShort,
            companyEngNameShortOptions,
        ],
        [LEGAL_YANDEX_ACCOUNT_TYPE_FIELD]: [accountType, setAccountType, accountTypeOptions],
        [LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD]: [accountCurrency, setAccountCurrency, accountCurrencyOptions],
        [YandexDocumentSelectField.ya_bank_name]: [bankName, setBankName, bankNameOptionsFromDetails],
        [YandexDocumentSelectField.ya_bic]: [bic, setBic, bicOptionsFromDetails],
        [YandexDocumentSelectField.ya_payment_account]: [paymentAccount, setPaymentAccount, paymentAccountOptions],
        [YandexDocumentSelectField.ya_corr_account]: [corrAccount, setCorrAccount, corrAccountOptions],
    };

    const selectVariablesMappingRef = useRef(selectVariablesMapping);
    selectVariablesMappingRef.current = selectVariablesMapping;

    const selectableOrgs = useMemo(() => {
        const optionsList = (component.optionsList as YandexListTypes) || DEFAULT_OPTIONS_LIST;
        return collectSelectableOrgsForLegalYandexDocument(orgs, {
            optionsList,
            list: component.list,
            bgBlackList: component.bgBlackList,
            buBlackList: component.buBlackList,
            countriesBlackList: component.countriesBlackList,
            additionalCompanies: component.additionalCompanies,
            bankDetailsFilter: component.bankDetailsFilter,
        });
    }, [
        orgs,
        component.optionsList,
        component.list,
        component.bgBlackList,
        component.buBlackList,
        component.countriesBlackList,
        component.additionalCompanies,
        component.bankDetailsFilter,
    ]);

    selectableOrgsRef.current = selectableOrgs;
    bankDetailsRef.current = bankDetails;
    componentRef.current = component;
    externalUpdateRecordRef.current = externalUpdateRecord;
    externalUpdateModeRef.current = externalUpdateMode;
    selectedOrgYaIdRef.current =
        selectedOrgYaId != null && String(selectedOrgYaId) !== "" ? String(selectedOrgYaId) : null;

    useEffect(() => {
        setSelectedOrgYaId(normalizeYaIdFromOption(companyName));
    }, [companyName?.value]);

    useEffect(() => {
        setSelectedOrgYaId(normalizeYaIdFromOption(companyEngNameShort));
    }, [companyEngNameShort?.value]);

    useEffect(() => {
        suppressSingleRowBankAutoFillRef.current = false;
        lastAppliedBankDetailFingerprintRef.current = "";
    }, [selectedOrgYaId]);

    useEffect(() => {
        const merged = {
            ...(pendingExternalBankApplyRef.current ?? {}),
            ...externalUpdateRecord,
        };
        pendingExternalBankApplyRef.current = merged;
        pendingExternalOrgApplyRef.current = {
            ...(pendingExternalOrgApplyRef.current ?? {}),
            ...externalUpdateRecord,
        };
    }, [externalUpdateRecord]);

    /**
     * Применяет накопленный снимок pendingExternalBankApplyRef: сначала банковские селекты, затем текстовые поля.
     * Снимок не очищается — после выбора счёта эффект снова подставляет реквизиты из API; текст из predefined
     * донакладывается через reapplyExternalBankTextFieldsFromRef.
     */
    const consumePendingExternalBankFields = useCallback((comp: ITemplateYandexComponent) => {
        const snap = pendingExternalBankApplyRef.current;
        if (!snap) {
            return;
        }

        const suffix = comp.suffix ? comp.suffix : "";
        const fieldsList = comp.fieldsList;
        if (!fieldsList?.length) {
            return;
        }

        let hasAnyBankKey = false;
        for (const key of legalYandexBankFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            if (Object.prototype.hasOwnProperty.call(snap, `${key}${suffix}`)) {
                hasAnyBankKey = true;
                break;
            }
        }
        if (!hasAnyBankKey) {
            return;
        }

        const mapping = selectVariablesMappingRef.current;
        const typeOnForm = fieldsList.includes(LEGAL_YANDEX_ACCOUNT_TYPE_FIELD);
        const currencyOnForm = fieldsList.includes(LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD);

        const recordPatch: Record<string, string | undefined> = {};

        for (const key of legalYandexBankFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            const extKey = `${key}${suffix}`;
            if (!Object.prototype.hasOwnProperty.call(snap, extKey)) {
                continue;
            }
            if (!isLegalYandexSelectLikeBankField(key)) {
                continue;
            }
            if (key === LEGAL_YANDEX_ACCOUNT_TYPE_FIELD && !typeOnForm) {
                continue;
            }
            if (key === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD && !currencyOnForm) {
                continue;
            }

            const raw = snap[extKey];
            const strVal = raw == null || raw === "" ? undefined : String(raw);
            const entry = mapping[key];
            if (!entry) {
                continue;
            }
            if (bankDetailsRef.current.length > 0) {
                const [, setValue] = entry;
                setValue(strVal === undefined ? undefined : { value: strVal, label: strVal });
                /** Не затирать record пустым predefined, если реквизиты уже пришли из API (replaceListed). */
                if (strVal !== undefined) {
                    recordPatch[key] = strVal;
                }
            } else {
                recordPatch[key] = strVal;
            }
        }

        for (const key of legalYandexBankFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            if (isLegalYandexSelectLikeBankField(key)) {
                continue;
            }
            const extKey = `${key}${suffix}`;
            if (!Object.prototype.hasOwnProperty.call(snap, extKey)) {
                continue;
            }
            const raw = snap[extKey];
            if (raw == null || raw === "") {
                continue;
            }
            recordPatch[key] = String(raw);
        }

        if (Object.keys(recordPatch).length > 0) {
            setRecord((prev) => ({ ...prev, ...recordPatch }));
        }
    }, []);

    const consumePendingExternalOrgFields = useCallback((comp: ITemplateYandexComponent) => {
        const snap = pendingExternalOrgApplyRef.current;
        if (!snap) {
            return;
        }
        const suffix = comp.suffix ? comp.suffix : "";
        const fieldsList = comp.fieldsList;
        if (!fieldsList?.length) {
            return;
        }

        let hasAnyOrgKey = false;
        for (const key of legalYandexOrganizationFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            if (Object.prototype.hasOwnProperty.call(snap, `${key}${suffix}`)) {
                hasAnyOrgKey = true;
                break;
            }
        }
        if (!hasAnyOrgKey) {
            return;
        }

        const mapping = selectVariablesMappingRef.current;
        const recordPatch: Record<string, string | undefined> = {};

        for (const key of legalYandexOrganizationFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            const extKey = `${key}${suffix}`;
            if (!Object.prototype.hasOwnProperty.call(snap, extKey)) {
                continue;
            }
            const raw = snap[extKey];
            const strVal = raw == null || raw === "" ? undefined : String(raw);

            /** Поля организации, которые на форме — SearchableSelect (как isShowSelect, без банковских ключей). */
            if (selectFieldsRef.current.includes(key)) {
                const entry = mapping[key];
                if (!entry || entry.length < 3) {
                    continue;
                }
                const [, setValue, options] = entry as [unknown, (o: unknown) => void, OptionType[]];
                const opt = resolveLegalYandexOrgSelectOption(strVal, options);
                setValue(opt === undefined ? undefined : opt);
                if (strVal !== undefined) {
                    recordPatch[key] = strVal;
                }
                continue;
            }

            if (raw == null || raw === "") {
                continue;
            }
            recordPatch[key] = String(raw);
        }

        if (Object.keys(recordPatch).length > 0) {
            setRecord((prev) => ({ ...prev, ...recordPatch }));
        }
        pendingExternalOrgApplyRef.current = null;
    }, []);

    /**
     * После updateRecordWithObject(detail) из API — снова выставить поля из merged external в record.
     * Если реквизитов нет, «селектные» поля тоже пишем в record (рендерятся как текст).
     * Если реквизиты есть — в record только не-селектные (значения селектов не перетираем).
     */
    const reapplyExternalBankTextFieldsFromRef = useCallback((comp: ITemplateYandexComponent | null | undefined) => {
        if (!comp) {
            return;
        }
        const snap = pendingExternalBankApplyRef.current;
        if (!snap) {
            return;
        }
        const suffix = comp.suffix ? comp.suffix : "";
        const fieldsList = comp.fieldsList;
        if (!fieldsList?.length) {
            return;
        }

        const noBankDetails = bankDetails.length === 0;
        const typeOnForm = fieldsList.includes(LEGAL_YANDEX_ACCOUNT_TYPE_FIELD);
        const currencyOnForm = fieldsList.includes(LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD);

        const recordPatch: Record<string, string | undefined> = {};
        for (const key of legalYandexBankFields) {
            if (!fieldsList.includes(key)) {
                continue;
            }
            if (key === LEGAL_YANDEX_ACCOUNT_TYPE_FIELD && !typeOnForm) {
                continue;
            }
            if (key === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD && !currencyOnForm) {
                continue;
            }
            const extKey = `${key}${suffix}`;
            if (!Object.prototype.hasOwnProperty.call(snap, extKey)) {
                continue;
            }
            if (isLegalYandexSelectLikeBankField(key) && !noBankDetails) {
                continue;
            }
            const raw = snap[extKey];
            /** Пустой predefined не должен затирать значения из replaceListed(detail) (в т.ч. первый выбор банка на внешней форме). */
            if (raw == null || raw === "") {
                continue;
            }
            recordPatch[key] = String(raw);
        }
        if (Object.keys(recordPatch).length > 0) {
            setRecord((prev) => ({ ...prev, ...recordPatch }));
        }
    }, [bankDetails.length]);

    useEffect(() => {
        if (bankDetails.length === 0) {
            reapplyExternalBankTextFieldsFromRef(componentRef.current);
        }
    }, [bankDetails.length, externalUpdateRecord, reapplyExternalBankTextFieldsFromRef]);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        return () => {
            const recordChanges: Record<string, any> = {};
            const suffix = component.suffix ? component.suffix : "";
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, `legalYandex_${suffix}`);

            fullYandexLegalFields.forEach((key) => {
                recordChanges[`${key}${suffix}`] = undefined;
            });
            onChange(recordChanges);
        };
    }, []);

    useEffect(() => {
        const recordChanges: Record<string, any> = {};
        const suffix = component.suffix ? component.suffix : "";
        fullYandexLegalFields.forEach((key) => {
            recordChanges[`${key}${suffix}`] = record[key] ? record[key] : undefined;
        });
        onChange(recordChanges);
    }, [record, component.suffix]);

    useEffect(() => {
        const orgIds = companyNameOptions.map((o) => String(o.value));
        const orgByInn = selectableOrgs.filter((o) => o.ya_inn === inn && orgIds.includes(String(o.ya_id)));
        if (inn && orgByInn.length > 0) {
            setCompanyName((prev) => {
                const next = companyNameOptions.find((o) => String(o.value) === String(orgByInn[0].ya_id));
                if (prev && next && String(prev.value) === String(next.value) && prev.label === next.label) {
                    return prev;
                }
                return next;
            });
        }
    }, [inn, companyNameOptions, selectableOrgs]);

    useEffect(() => {
        const orgIds = companyNameOptions.map((o) => String(o.value));
        const r = regNum.trim();
        if (!r) {
            return;
        }
        const orgByRegNum = selectableOrgs.filter(
            (o) =>
                o.ya_reg_num != null
                && String(o.ya_reg_num).trim() === r
                && orgIds.includes(String(o.ya_id))
        );
        if (orgByRegNum.length > 0) {
            setCompanyName((prev) => {
                const next = companyNameOptions.find((o) => String(o.value) === String(orgByRegNum[0].ya_id));
                if (prev && next && String(prev.value) === String(next.value) && prev.label === next.label) {
                    return prev;
                }
                return next;
            });
        }
    }, [regNum, companyNameOptions, selectableOrgs]);

    /**
     * Выбор организации: URL/external всегда выше defaultValue.
     * Если в external есть ключи идентичности, но ya_id ещё не резолвится (orgs грузятся) —
     * defaultValue не применяем, ждём резолва.
     */
    useEffect(() => {
        if (companyNameOptions.length === 0) {
            return;
        }
        const suffix = component.suffix ? component.suffix : "";
        const resolvedYaId = resolveOrgYaIdFromExternal(
            externalUpdateRecord,
            suffix,
            selectableOrgs,
        );
        const hasExternalIdentity = hasExternalOrgIdentityKeys(externalUpdateRecord, suffix);

        if (resolvedYaId) {
            if (resolvedYaId !== lastLoadedOrgYaIdRef.current) {
                setSelectedOrgYaId(resolvedYaId);
            } else {
                updateWithExternalData(componentRef.current);
                window.setTimeout(() => {
                    consumePendingExternalBankFields(componentRef.current);
                    consumePendingExternalOrgFields(componentRef.current);
                }, 100);
            }
            return;
        }

        if (hasExternalIdentity) {
            return;
        }

        if (externalUpdateMode === "update") {
            clearForm();
            return;
        }

        if (component.defaultValue && !isExternalUser) {
            setSelectedOrgYaId(component.defaultValue);
        }
    }, [
        externalUpdateRecord,
        externalUpdateMode,
        component.suffix,
        component.fieldsList,
        component.defaultValue,
        companyNameOptions,
        companyEngNameShortOptions,
        selectableOrgs,
        isExternalUser,
    ]);

    useLayoutEffect(() => {
        if (!hasAccountTypeFieldOnForm) {
            prevAccountTypeValueRef.current = "";
        }
        if (!hasAccountCurrencyFieldOnForm) {
            prevAccountCurrencyValueRef.current = "";
        }
    }, [hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]);

    useEffect(() => {
        if (!hasAccountTypeFieldOnForm) {
            setAccountType(undefined);
        }
        if (!hasAccountCurrencyFieldOnForm) {
            setAccountCurrency(undefined);
        }
    }, [hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]);

    useEffect(() => {
        updateRecordWithObject(
            {
                ya_account_type:
                    hasAccountTypeFieldOnForm
                    && accountType?.value != null
                    && String(accountType.value) !== ""
                        ? String(accountType.value)
                        : undefined,
                ya_account_currency:
                    hasAccountCurrencyFieldOnForm
                    && accountCurrency?.value != null
                    && String(accountCurrency.value) !== ""
                        ? String(accountCurrency.value)
                        : undefined,
            },
            ["ya_account_type", "ya_account_currency"]
        );
    }, [accountType, accountCurrency, hasAccountTypeFieldOnForm, hasAccountCurrencyFieldOnForm]);

    useEffect(() => {
        const hasTypeField = hasAccountTypeFieldOnForm;
        const hasCurrencyField = hasAccountCurrencyFieldOnForm;
        const tNow = hasTypeField ? optionValueAsString(accountType) : "";
        const cNow = hasCurrencyField ? optionValueAsString(accountCurrency) : "";
        const bnNow = optionValueAsString(bankName);
        const bicNow = optionValueAsString(bic);

        const tPrev = hasTypeField ? prevAccountTypeValueRef.current : "";
        const cPrev = hasCurrencyField ? prevAccountCurrencyValueRef.current : "";
        const bnPrev = prevBankNameValueRef.current;
        const bicPrev = prevBicValueRef.current;
        const paNow = optionValueAsString(paymentAccount);
        const corrNow = optionValueAsString(corrAccount);
        const paPrev = prevPaymentAccountValueRef.current;
        const corrPrev = prevCorrAccountValueRef.current;

        const stripMergedExternalBankKeysForComponent = (preserveAccountCurrencyKey?: boolean) => {
            const suffix = component.suffix ? component.suffix : "";
            const snap = pendingExternalBankApplyRef.current;
            if (!snap) {
                return;
            }
            const next = { ...snap };
            let changed = false;
            for (const key of legalYandexBankFields) {
                if (preserveAccountCurrencyKey && key === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD) {
                    continue;
                }
                const extKey = `${key}${suffix}`;
                if (Object.prototype.hasOwnProperty.call(next, extKey)) {
                    delete next[extKey];
                    changed = true;
                }
            }
            if (changed) {
                pendingExternalBankApplyRef.current = Object.keys(next).length > 0 ? next : null;
            }
        };

        const preserveAccountCurrencyInRecord =
            hasCurrencyField && cNow !== "";

        const clearBankChain = () => {
            stripMergedExternalBankKeysForComponent(preserveAccountCurrencyInRecord);
            suppressBankChainReconcileRef.current = true;
            setBankName(undefined);
            setBic(undefined);
            setPaymentAccount(undefined);
            setCorrAccount(undefined);
            setPaymentAccountOptions([]);
            setCorrAccountOptions([]);
            setRecord((prev) => {
                const next = { ...prev };
                legalYandexBankFields.forEach((k) => {
                    if (preserveAccountCurrencyInRecord && k === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD) {
                        return;
                    }
                    next[k] = undefined;
                });
                return next;
            });
            prevBankNameValueRef.current = "";
            prevBicValueRef.current = "";
            prevPaymentAccountValueRef.current = "";
            prevCorrAccountValueRef.current = "";
        };

        const clearAllBankFields = (opts?: { preserveAccountCurrency?: boolean }) => {
            const preserveCurrency = Boolean(opts?.preserveAccountCurrency);
            stripMergedExternalBankKeysForComponent(
                preserveCurrency && hasCurrencyField
            );
            suppressBankChainReconcileRef.current = true;
            setAccountType(undefined);
            if (!preserveCurrency) {
                setAccountCurrency(undefined);
            }
            setBankName(undefined);
            setBic(undefined);
            setPaymentAccount(undefined);
            setCorrAccount(undefined);
            setPaymentAccountOptions([]);
            setCorrAccountOptions([]);
            setRecord((prev) => {
                const next = { ...prev };
                legalYandexBankFields.forEach((k) => {
                    if (preserveCurrency && k === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD) {
                        return;
                    }
                    next[k] = undefined;
                });
                return next;
            });
            prevAccountTypeValueRef.current = "";
            prevAccountCurrencyValueRef.current = preserveCurrency && hasCurrencyField
                ? optionValueAsString(accountCurrency)
                : "";
            prevBankNameValueRef.current = "";
            prevBicValueRef.current = "";
            prevPaymentAccountValueRef.current = "";
            prevCorrAccountValueRef.current = "";
        };

        const clearTypeAndBankChain = () => {
            setAccountType(undefined);
            clearBankChain();
            setRecord((prev) => ({ ...prev, ya_account_type: undefined }));
            prevAccountTypeValueRef.current = "";
        };

        const bankCleared = bnPrev !== "" && bnNow === "";
        const bicCleared = bicPrev !== "" && bicNow === "";
        const paymentCleared = paPrev !== "" && paNow === "";
        const corrCleared = corrPrev !== "" && corrNow === "";
        const accountTypeClearedByUser = hasTypeField && tPrev !== "" && tNow === "";
        const accountCurrencyClearedByUser = hasCurrencyField && cPrev !== "" && cNow === "";

        if (
            bankDetails.length === 1
            && (bankCleared
                || bicCleared
                || paymentCleared
                || corrCleared
                || accountTypeClearedByUser
                || accountCurrencyClearedByUser)
        ) {
            suppressSingleRowBankAutoFillRef.current = true;
            suppressBankChainReconcileRef.current = true;
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields({ preserveAccountCurrency: !accountCurrencyClearedByUser });
            return;
        }

        // Валюта снята — сбрасываем все банковские поля
        if (cPrev !== "" && cNow === "") {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields();
            return;
        }

        // Валюта сменилась на другую — тип, банк, счета очищаем (валюта уже новая в state)
        if (cNow !== "" && cPrev !== "" && cNow !== cPrev) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearTypeAndBankChain();
            prevAccountCurrencyValueRef.current = cNow;
            return;
        }

        // Тип снят — валюту оставляем, если поле валюты на форме и валюта выбрана; иначе (валюты на форме нет) всё равно чистим цепочку банка
        if (tPrev !== "" && tNow === "" && (!hasCurrencyField || cNow !== "")) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearBankChain();
            setRecord((prev) => ({ ...prev, ya_account_type: undefined }));
            prevAccountTypeValueRef.current = "";
            prevAccountCurrencyValueRef.current = hasCurrencyField ? cNow : "";
            return;
        }

        // Тип сменили на другой — цепочку банка/счетов сбрасываем
        if (tNow !== "" && tPrev !== "" && tNow !== tPrev) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearBankChain();
            prevAccountTypeValueRef.current = tNow;
            prevAccountCurrencyValueRef.current = hasCurrencyField ? cNow : "";
            return;
        }

        if (bankCleared) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields({ preserveAccountCurrency: true });
            return;
        }

        if (bicCleared) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields({ preserveAccountCurrency: true });
            return;
        }

        if (paymentCleared) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields({ preserveAccountCurrency: true });
            return;
        }

        if (corrCleared) {
            suppressPaymentRowCurrencyTypeSyncRef.current = true;
            clearAllBankFields({ preserveAccountCurrency: true });
            return;
        }

        prevAccountTypeValueRef.current = hasTypeField ? tNow : "";
        prevAccountCurrencyValueRef.current = hasCurrencyField ? cNow : "";
        prevBankNameValueRef.current = bnNow;
        prevBicValueRef.current = bicNow;
        prevPaymentAccountValueRef.current = paNow;
        prevCorrAccountValueRef.current = corrNow;
    }, [
        accountType,
        accountCurrency,
        bankName,
        bic,
        paymentAccount,
        corrAccount,
        bankDetails.length,
        bankDetailScope,
        hasAccountTypeFieldOnForm,
        hasAccountCurrencyFieldOnForm,
        component.suffix,
    ]);

    useEffect(() => {
        if (!hasAccountCurrencyFieldOnForm) {
            return;
        }
        const v = accountCurrency?.value;
        if (v == null || String(v) === "") {
            return;
        }
        const still = accountCurrencyOptions.some((o) => String(o.value) === String(v));
        if (!still) {
            setAccountCurrency(undefined);
        }
    }, [accountCurrencyOptions, accountCurrency?.value, hasAccountCurrencyFieldOnForm]);

    useEffect(() => {
        if (!hasAccountTypeFieldOnForm) {
            return;
        }
        const v = accountType?.value;
        if (v == null || String(v) === "") {
            return;
        }
        const still = accountTypeOptions.some((o) => String(o.value) === String(v));
        if (!still) {
            setAccountType(undefined);
        }
    }, [accountTypeOptions, accountType?.value, hasAccountTypeFieldOnForm]);

    useEffect(() => {
        if (!bankDetails.length) {
            return;
        }
        if (!bankDetailScope.length) {
            const scopeNarrowedByFilters =
                (hasAccountCurrencyFieldOnForm && !!currencyKey)
                || (hasAccountTypeFieldOnForm && !!typeKey);
            if (scopeNarrowedByFilters && (bankName?.value || bic?.value)) {
                setBankName(undefined);
                setBic(undefined);
                setPaymentAccount(undefined);
                setCorrAccount(undefined);
            }
            return;
        }
        if (bankName?.value && !bankDetailScope.some((d) => d.ya_bank_name === bankName.value)) {
            setBankName(undefined);
            setBic(undefined);
            setPaymentAccount(undefined);
            setCorrAccount(undefined);
        }
        if (bic?.value && !bankDetailScope.some((d) => d.ya_bic === bic.value)) {
            setBankName(undefined);
            setBic(undefined);
            setPaymentAccount(undefined);
            setCorrAccount(undefined);
        }
    }, [
        bankDetails.length,
        bankDetailScope,
        currencyKey,
        typeKey,
        bankName?.value,
        bic?.value,
        hasAccountCurrencyFieldOnForm,
        hasAccountTypeFieldOnForm,
    ]);

    useEffect(() => {
        if (suppressBankChainReconcileRef.current) {
            suppressBankChainReconcileRef.current = false;
            bankChainUiLeadRef.current = null;
            return;
        }

        const lead = bankChainUiLeadRef.current;
        bankChainUiLeadRef.current = null;

        updateRecordWithObject({ ya_bank_name: bankName?.value,  ya_bic: bic?.value }, ["ya_bank_name", "ya_bic"]);

        if (!bankDetailScope.length) {
            return;
        }

        if (!bankName?.value && !bic?.value) {
            return;
        }

        if (lead === "bank" && !bankName?.value) {
            return;
        }

        if (lead === "bic" && !bic?.value) {
            return;
        }

        let resolvedBic: string | undefined;
        let resolvedBankNameForRows: string | undefined;

        if (lead === "bank" && bankName?.value) {
            const forBank = bankDetailScope.filter((d) => d.ya_bank_name === bankName.value);
            if (forBank.length === 0) {
                updateRecordWithObject({ ya_bic: bic?.value }, ["ya_bic"]);
                return;
            }
            resolvedBic = forBank[0].ya_bic;
            resolvedBankNameForRows = bankName.value;
        } else if (lead === "bic" && bic?.value) {
            resolvedBic = bic.value;
            resolvedBankNameForRows = bankDetailScope.find((d) => d.ya_bic === bic.value)?.ya_bank_name;
        } else if (bankName?.value) {
            const forBank = bankDetailScope.filter((d) => d.ya_bank_name === bankName.value);
            if (forBank.length === 0) {
                updateRecordWithObject({ ya_bic: bic?.value }, ["ya_bic"]);
                return;
            }
            const bicStillValid =
                bic?.value && forBank.some((d) => d.ya_bic === bic.value);
            resolvedBic = bicStillValid ? bic.value : forBank[0].ya_bic;
            resolvedBankNameForRows = bankName.value;
        } else if (bic?.value) {
            resolvedBic = bic.value;
            resolvedBankNameForRows = bankDetailScope.find((d) => d.ya_bic === bic.value)?.ya_bank_name;
        } else {
            updateRecordWithObject({ ya_bic: undefined }, ["ya_bic"]);
            return;
        }

        const filteredDetails = bankDetailScope.filter(
            (d) =>
                d.ya_bic === resolvedBic
                && (resolvedBankNameForRows == null || d.ya_bank_name === resolvedBankNameForRows)
        );

        const paymentAccountOpts = new Set<string>();
        const corrAccountOpts = new Set<string>();
        filteredDetails.forEach((detail) => {
            paymentAccountOpts.add(detail.ya_payment_account);
            corrAccountOpts.add(detail.ya_corr_account);
        });
        const corrAccountOptions = constructOptions(Array.from(corrAccountOpts));
        const payAccountOptions = constructOptions(Array.from(paymentAccountOpts));
        setCorrAccountOptions(corrAccountOptions);
        setPaymentAccountOptions(payAccountOptions);

        if (bic?.value !== resolvedBic) {
            setBic(constructOptions([resolvedBic])[0]);
        }
        if (resolvedBankNameForRows && bankName?.value !== resolvedBankNameForRows) {
            setBankName(constructOptions([resolvedBankNameForRows])[0]);
        }
        if (!paymentAccount?.value || !payAccountOptions.some((o) => o.value === paymentAccount.value)) {
            setPaymentAccount(payAccountOptions[0]);
        }
        if (!corrAccount?.value || !corrAccountOptions.some((o) => o.value === corrAccount.value)) {
            setCorrAccount(corrAccountOptions[0]);
        }

        updateRecordWithObject({ ya_bic: resolvedBic }, ["ya_bic"]);
    }, [bankName, bic, bankDetailScope]);

    useEffect(() => {
        try {
            if (
                bankDetailScope.length === 1
                && !suppressSingleRowBankAutoFillRef.current
                && !bankName?.value
                && !clearBankActionTriggered.current
            ) {
                setBankName({label: bankDetailScope[0].ya_bank_name, value: bankDetailScope[0].ya_bank_name});
                if (hasAccountTypeFieldOnForm) {
                    setAccountType({label: bankDetailScope[0].ya_account_type ?? "", value: bankDetailScope[0].ya_account_type ?? ""})
                }
                if (hasAccountCurrencyFieldOnForm) {
                    setAccountCurrency({label: bankDetailScope[0].ya_account_currency ?? "", value: bankDetailScope[0].ya_account_currency ?? ""})
                }
                return;
            }
            if (clearBankActionTriggered.current) {
                clearBankActionTriggered.current = false;
            }
            if (!bankDetailScope.length || !bankName?.value || !paymentAccount?.value) {
                if (suppressPaymentRowCurrencyTypeSyncRef.current) {
                    suppressPaymentRowCurrencyTypeSyncRef.current = false;
                }
                updateRecordWithObject(
                    { ya_payment_account: paymentAccount?.value },
                    ["ya_payment_account"]
                );
                return;
            }
            const sameBank = (d: ILegalOrgBankDetail) => d.ya_bank_name === bankName.value;
            const isRub = (d: ILegalOrgBankDetail) =>
                String(d.ya_account_currency ?? "").toUpperCase() === "RUB";
            const paymentAccountIsPrimary = (d: ILegalOrgBankDetail) =>
                String(d.ya_account_type ?? "").toLowerCase() === "основной";
            const paymentAccountIndicatesPrimary = (d: ILegalOrgBankDetail) =>
                String(d.ya_account_type ?? "").toLowerCase().includes("основной");

            const detail =
                bankDetailScope.find((d) => sameBank(d) && isRub(d) && paymentAccountIsPrimary(d))
                ?? bankDetailScope.find((d) => sameBank(d) && isRub(d) && paymentAccountIndicatesPrimary(d))
                ?? bankDetailScope.find((d) => sameBank(d) && paymentAccountIndicatesPrimary(d))
                ?? bankDetailScope.find(
                    (d) =>
                        sameBank(d) && d.ya_payment_account === paymentAccount.value
                );

            if (detail) {
                if (suppressPaymentRowCurrencyTypeSyncRef.current) {
                    suppressPaymentRowCurrencyTypeSyncRef.current = false;
                    return;
                }
                if (hasAccountTypeFieldOnForm && detail.ya_account_type) {
                    const nextType = constructOptions([detail.ya_account_type])[0];
                    setAccountType((prev) =>
                        prev?.value === nextType.value && prev?.label === nextType.label ? prev : nextType
                    );
                }
                if (hasAccountCurrencyFieldOnForm && detail.ya_account_currency) {
                    const nextCur = constructOptions([detail.ya_account_currency])[0];
                    setAccountCurrency((prev) =>
                        prev?.value === nextCur.value && prev?.label === nextCur.label ? prev : nextCur
                    );
                }
                const sfx = componentRef.current.suffix ? componentRef.current.suffix : "";
                const fp = bankDetailRowFingerprint(detail);
                const prevFp = lastAppliedBankDetailFingerprintRef.current;
                if (prevFp !== "" && prevFp !== fp) {
                    stripNonSelectBankTextKeysFromPendingExternal(pendingExternalBankApplyRef, sfx);
                }
                lastAppliedBankDetailFingerprintRef.current = fp;
                updateRecordWithObject(detail, legalYandexBankFieldsForForm, "replaceListed");
            } else {
                if (suppressPaymentRowCurrencyTypeSyncRef.current) {
                    suppressPaymentRowCurrencyTypeSyncRef.current = false;
                }
                updateRecordWithObject(
                    { ya_payment_account: paymentAccount?.value },
                    ["ya_payment_account"]
                );
            }
        } finally {
            reapplyExternalBankTextFieldsFromRef(componentRef.current);
        }
    }, [
        paymentAccount,
        bankName,
        bankDetailScope,
        hasAccountTypeFieldOnForm,
        hasAccountCurrencyFieldOnForm,
        reapplyExternalBankTextFieldsFromRef,
    ]);

    useEffect(() => {
        updateRecordWithObject({ ya_corr_account: corrAccount?.value }, ["ya_corr_account"]);
    }, [corrAccount]);

    const updateByCompanyId = useCallback((companyYaId: string, requestGen: number) => {
        const selectedOrg = selectableOrgsRef.current.find((o) => String(o.ya_id) === String(companyYaId));
        const comp = componentRef.current;
        legalOrgsService.getOrg(companyYaId, legalOrgsExtUserId).then((org: ILegalOrg) => {
            if (requestGen !== orgFetchGenerationRef.current) return;
            if (String(companyYaId) !== String(selectedOrgYaIdRef.current ?? "")) return;

            pendingFetchYaIdRef.current = null;
            lastLoadedOrgYaIdRef.current = String(companyYaId);
            lastAppliedBankDetailFingerprintRef.current = "";

            /** Смена компании: не мержить с предыдущим record — иначе поля, которых нет у новой орг., остаются от старой. */
            clearBankDetails();
            const nextRecord: Record<string, string | undefined> = {};
            for (const key of fullYandexLegalFields) {
                nextRecord[key] = undefined;
            }
            if (org) {
                const orgInn = org.ya_inn;
                setInn(orgInn ? orgInn : "");
                setRegNum(org.ya_reg_num != null && String(org.ya_reg_num) !== "" ? String(org.ya_reg_num) : "");
                const countryRu = getCountryRuNameByIsoCode(org.ya_country_code);
                const src = {
                    ...org,
                    ...(countryRu != null ? { ya_country: countryRu } : {}),
                } as Record<string, any>;
                for (const f of legalYandexOrganizationFields) {
                    if (!Object.prototype.hasOwnProperty.call(src, f)) {
                        continue;
                    }
                    const v = src[f];
                    nextRecord[f] = v == null || v === "" ? undefined : String(v);
                }
            } else {
                setInn("");
                setRegNum("");
                const name = selectedOrg?.ya_name;
                if (name != null && String(name) !== "") {
                    nextRecord[YandexDocumentSelectField.ya_name] = String(name);
                }
            }
            setRecord(nextRecord);

            const orgBankDetails = org?.ya_bank_details
                ? org.ya_bank_details.filter((o: ILegalOrgBankDetail) => !!o)
                : [];
            if (orgBankDetails.length > 0) {
                if (comp.optionsList === YandexListTypes.yaOrgs || !comp.optionsList) {
                    const companyLegal = comp.listLegalBankDetails?.find(
                        (d) => String(d.id) === String(companyYaId) || d.yaName === selectedOrg?.ya_name
                    );
                    if (companyLegal) {
                        updateBankDetailsFromLegal(orgBankDetails, companyLegal);
                    } else if (comp.listBankDetails) {
                        const companyBankDetails = comp.listBankDetails?.find(
                            (d) => d.item === selectedOrg?.ya_name || d.item === companyYaId
                        );
                        if (companyBankDetails && companyBankDetails.bankNameWhiteList.length > 0) {
                            updateBankDetails(orgBankDetails, companyBankDetails);
                        } else {
                            updateBankDetailsFromLegal(orgBankDetails, { bankBlackListArray: [] });
                        }
                    } else {
                        updateBankDetailsFromLegal(orgBankDetails, { bankBlackListArray: [] });
                    }
                } else {
                    const filteredByRules = filterBankDetailsByListFilter(org.ya_bank_details, comp.bankDetailsFilter);
                    updateBankDetailsFromLegal(filteredByRules, { bankBlackListArray: [] });
                }
            }
            // После ответа API — один раз подставляем bank/org из отдельных pending-снимков (как merge external).
            setTimeout(() => {
                if (requestGen !== orgFetchGenerationRef.current) return;
                if (String(companyYaId) !== String(selectedOrgYaIdRef.current ?? "")) return;
                consumePendingExternalBankFields(comp);
                consumePendingExternalOrgFields(comp);
            }, 250);
        }).catch(() => {
            if (requestGen === orgFetchGenerationRef.current) {
                pendingFetchYaIdRef.current = null;
                lastLoadedOrgYaIdRef.current = null;
                pendingExternalBankApplyRef.current = null;
                pendingExternalOrgApplyRef.current = null;
                suppressSingleRowBankAutoFillRef.current = false;
                lastAppliedBankDetailFingerprintRef.current = "";
                setInn("");
                setRegNum("");
                setRecord({});
                setCompanyName(undefined);
                setCompanyEngNameShort(undefined);
                clearBankDetails();
            }
        });
    }, [legalOrgsExtUserId, consumePendingExternalBankFields, consumePendingExternalOrgFields]);

    useEffect(() => {
        const id = selectedOrgYaId;
        if (!id) {
            pendingFetchYaIdRef.current = null;
            lastLoadedOrgYaIdRef.current = null;
            clearForm();
            return;
        }
        if (lastLoadedOrgYaIdRef.current === id) {
            return;
        }
        if (pendingFetchYaIdRef.current === id) {
            return;
        }
        pendingFetchYaIdRef.current = id;
        const requestGen = ++orgFetchGenerationRef.current;
        updateByCompanyId(id, requestGen);
    }, [selectedOrgYaId, updateByCompanyId]);

    useEffect(() => {
        const id = selectedOrgYaId;
        if (!id) return;
        const org = selectableOrgs.find((o) => String(o.ya_id) === id);
        if (!org) return;
        setCompanyName((prev) => {
            const next = companyNameOptions.find((o) => String(o.value) === org.ya_id);
            if (prev && next && String(prev.value) === String(next.value) && prev.label === next.label) {
                return prev;
            }
            return next;
        });
        setCompanyEngNameShort((prev) => {
            const next = companyEngNameShortOptions.find((o) => String(o.value) === org.ya_id);
            if (prev && next && String(prev.value) === String(next.value) && prev.label === next.label) {
                return prev;
            }
            return next;
        });
    }, [selectedOrgYaId, selectableOrgs, companyNameOptions, companyEngNameShortOptions]);

    const clearBankDetails = () => {
        setBankDetails([]);
        setBankName(undefined);
        setBic(undefined);
        setAccountType(undefined);
        setAccountCurrency(undefined);
        setPaymentAccountOptions([]);
        setPaymentAccount(undefined);
        setCorrAccountOptions([]);
        setCorrAccount(undefined);
        prevAccountTypeValueRef.current = "";
        prevAccountCurrencyValueRef.current = "";
        prevBankNameValueRef.current = "";
        prevBicValueRef.current = "";
        prevPaymentAccountValueRef.current = "";
        prevCorrAccountValueRef.current = "";
    };

    const updateWithExternalData = (comp: ITemplateYandexComponent) => {
        const updateChanges: Record<string, any> = {};
        const suffix = comp.suffix ? comp.suffix : "";
        const extRec = externalUpdateRecordRef.current;
        const extMode = externalUpdateModeRef.current;
        const mapping = selectVariablesMappingRef.current;
        const typeOnForm = comp.fieldsList?.includes(LEGAL_YANDEX_ACCOUNT_TYPE_FIELD) ?? false;
        const currencyOnForm = comp.fieldsList?.includes(LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD) ?? false;
        fullYandexLegalFields.forEach((key) => {
            const isBankSelect = [
                YandexDocumentSelectField.ya_bank_name,
                YandexDocumentSelectField.ya_bic,
                YandexDocumentSelectField.ya_payment_account,
                YandexDocumentSelectField.ya_corr_account,
            ].includes(key as YandexDocumentSelectField);
            const isAccountTypeSelect = key === LEGAL_YANDEX_ACCOUNT_TYPE_FIELD;
            const isAccountCurrencySelect = key === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD;

            if (isAccountTypeSelect && !typeOnForm) {
                updateChanges[key] = undefined;
                return;
            }
            if (isAccountCurrencySelect && !currencyOnForm) {
                updateChanges[key] = undefined;
                return;
            }

            if (isBankSelect || isAccountCurrencySelect || isAccountTypeSelect) {
                const option = extRec[`${key}${suffix}`] ? {
                    value: extRec[`${key}${suffix}`],
                    label: extRec[`${key}${suffix}`]
                } : undefined;

                const [, setValue] = mapping[key];
                if (option || extMode === "update") {
                    setValue(option);
                }
            }

            updateChanges[key] = extRec[`${key}${suffix}`] ? extRec[`${key}${suffix}`] : undefined;
        });
        setRecord((prevState) => {
            if (extMode === "update") {
                return updateChanges;
            }
            return { ...prevState, ...cleanUndefinedNullObject(updateChanges) };
        });
    }

    const updateBankDetailsFromLegal = (
        orgBankDetails: ILegalOrgBankDetail[],
        companyLegal: Pick<ILegalBankDetail, "bankBlackListArray" | "bankDefaultValue">
    ) => {
        const blacklist = new Set((companyLegal.bankBlackListArray ?? []).map((id) => String(id)));
        const filteredBankDetails = orgBankDetails.filter((detail) => !blacklist.has(String(detail?.id)));
        setBankDetails(filteredBankDetails);
        const defId = companyLegal.bankDefaultValue;
        if (defId && !isExternalUser) {
            const defaultBankValue = filteredBankDetails.find((detail) => String(detail.id) === String(defId));
            if (defaultBankValue) {
                if (hasAccountTypeFieldOnForm && defaultBankValue.ya_account_type) {
                    setAccountType(constructOptions([defaultBankValue.ya_account_type])[0]);
                }
                if (hasAccountCurrencyFieldOnForm && defaultBankValue.ya_account_currency) {
                    setAccountCurrency(constructOptions([defaultBankValue.ya_account_currency])[0]);
                }
                setBankName(constructOptions([defaultBankValue.ya_bank_name])[0]);
                setBic(constructOptions([defaultBankValue.ya_bic])[0]);
                setCorrAccount(constructOptions([defaultBankValue.ya_corr_account])[0]);
                setPaymentAccount(constructOptions([defaultBankValue.ya_payment_account])[0]);
                updateRecordWithObject(defaultBankValue, legalYandexBankFieldsForForm, "replaceListed");
                lastAppliedBankDetailFingerprintRef.current = bankDetailRowFingerprint(defaultBankValue);
                reapplyExternalBankTextFieldsFromRef(componentRef.current);
            }
        }
    };

    const updateBankDetails = (orgBankDetails: ILegalOrgBankDetail[], companyBankDetails: IBankDetail) => {
        const whitelist = new Set(companyBankDetails.bankNameWhiteList.map((id) => String(id)));
        const filteredBankDetails = orgBankDetails.filter((detail) => whitelist.has(String(detail.id)));
        setBankDetails(filteredBankDetails);
        if (companyBankDetails.bankNameDefaultValue && !isExternalUser) {
            const defaultBankValue = filteredBankDetails.find(
                (detail) => String(detail.id) === String(companyBankDetails.bankNameDefaultValue)
            );
            if (defaultBankValue) {
                if (hasAccountTypeFieldOnForm && defaultBankValue.ya_account_type) {
                    setAccountType(constructOptions([defaultBankValue.ya_account_type])[0]);
                }
                if (hasAccountCurrencyFieldOnForm && defaultBankValue.ya_account_currency) {
                    setAccountCurrency(constructOptions([defaultBankValue.ya_account_currency])[0]);
                }
                setBankName(constructOptions([defaultBankValue.ya_bank_name])[0]);
                setBic(constructOptions([defaultBankValue.ya_bic])[0]);
                setCorrAccount(constructOptions([defaultBankValue.ya_corr_account])[0]);
                setPaymentAccount(constructOptions([defaultBankValue.ya_payment_account])[0]);
                updateRecordWithObject(defaultBankValue, legalYandexBankFieldsForForm, "replaceListed");
                lastAppliedBankDetailFingerprintRef.current = bankDetailRowFingerprint(defaultBankValue);
                reapplyExternalBankTextFieldsFromRef(componentRef.current);
            }
        }
    };

    const handleClearEvent = () => {
        if (!isExternalProtected) {
            clearForm();
        }
    };

    const clearForm = () => {
        orgFetchGenerationRef.current += 1;
        pendingFetchYaIdRef.current = null;
        lastLoadedOrgYaIdRef.current = null;
        pendingExternalBankApplyRef.current = null;
        pendingExternalOrgApplyRef.current = null;
        suppressSingleRowBankAutoFillRef.current = false;
        lastAppliedBankDetailFingerprintRef.current = "";
        setInn("");
        setRegNum("");
        setRecord({});
        setCompanyName(undefined);
        setCompanyEngNameShort(undefined);
        clearBankDetails();
    };

    useEffect(() => {
        if (legalOrgsLoading || orgs.length === 0) {
            return;
        }
        const optionsList = (component.optionsList as YandexListTypes) || DEFAULT_OPTIONS_LIST;
        const isYaOrgsList = optionsList === YandexListTypes.yaOrgs;
        const priorityYaId = findPriorityYandexOrgYaId(selectableOrgs);
        const mapName = (o: (typeof selectableOrgs)[number]) => ({
            value: o.ya_id,
            label: o.ya_name,
        });
        const mapEngShort = (o: (typeof selectableOrgs)[number]) => ({
            value: o.ya_id,
            label: o.ya_eng_name_short ?? "",
        });
        if (isYaOrgsList) {
            setCompanyNameOptions(selectableOrgs.map(mapName));
            setCompanyEngNameShortOptions(selectableOrgs.map(mapEngShort));
        } else {
            setCompanyNameOptions(
                sortLegalOrgSelectOptionsFirstByYaId(selectableOrgs.map(mapName), priorityYaId)
            );
            setCompanyEngNameShortOptions(
                sortLegalOrgSelectOptionsFirstByYaId(selectableOrgs.map(mapEngShort), priorityYaId)
            );
        }
    }, [
        legalOrgsLoading,
        selectableOrgs,
        component.optionsList,
    ]);

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    /**
     * merge — только ключи, присутствующие в updateObj (старое поведение).
     * replaceListed — для каждого ключа из fieldsToUpdate задаётся значение из updateObj или undefined
     * (нужно при смене банка/счёта: текстовые банковские поля не должны оставаться от предыдущей строки).
     */
    const updateRecordWithObject = (
        updateObj: Record<string, any> | undefined,
        fieldsToUpdate: string[],
        mode: "merge" | "replaceListed" = "merge",
    ) => {
        const update: Record<string, string | undefined> = {};
        fieldsToUpdate.forEach((f) => {
            if (!updateObj) {
                update[f] = undefined;
                return;
            }
            if (mode === "replaceListed") {
                if (!Object.prototype.hasOwnProperty.call(updateObj, f)) {
                    update[f] = undefined;
                    return;
                }
                const v = updateObj[f];
                update[f] = v == null || v === "" ? undefined : String(v);
                return;
            }
            if (!Object.prototype.hasOwnProperty.call(updateObj, f)) {
                return;
            }
            const v = updateObj[f];
            update[f] = v == null || v === "" ? undefined : String(v);
        });
        setRecord((prevState) => {
            return { ...prevState, ...update };
        });
    };

    const validateFields = (): boolean => {
        const legalYandexFields = Object.keys(LegalYandexWordFields);
        return !!component.fieldsList?.every(field => {
            return !!record[field] || !legalYandexFields.includes(field);
        });
    };

    useEffect(() => {
        onValidationRegister(validateForm, `legalYandex_${component.suffix ? component.suffix : ""}`);
    }, [component.suffix, record, required]);

    const onFieldChange = (field: string, value: string | undefined) => {
        setRecord((prevState) => {
            return { ...prevState, [field]: value };
        });
    };

    const validateForm = (): Promise<boolean> => {
        let valid = true;
        if (required) {
            valid = validateFields();
        }
        return Promise.resolve(valid);
    };

    const isDateField = (field: string): boolean => {
        return field.includes("date");
    };

    const isShowSelect = (field: string) => {
        const isSelectField = selectFields.includes(field);
        if (isSelectField) {
            const [, , options] = selectVariablesMapping[field];
            if (
                field === YandexDocumentSelectField.ya_bank_name
                || field === YandexDocumentSelectField.ya_bic
            ) {
                return bankDetails.length > 0;
            }
            return options.length > 0;
        }
        return false;
    };

    const validateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }
        return {
            valid: true
        };
    }, [component, i18n.language]);

    const sortFieldList = (fieldList?: string[]): string[] => {
        if (!fieldList?.length) {
            return [];
        }
        const ordered: string[] = [];
        fullYandexLegalFields.forEach((field) => {
            if (fieldList.includes(field)) {
                ordered.push(field);
            }
        });
        fieldList.forEach((field) => {
            if (!ordered.includes(field)) {
                ordered.push(field);
            }
        });
        return ordered;
    };

    const renderSelect = (field: string) => {
        const [value, setValue, options] = selectVariablesMapping[field];
        const isCurrencyField = field === LEGAL_YANDEX_ACCOUNT_CURRENCY_FIELD;
        const isAccountTypeField = field === LEGAL_YANDEX_ACCOUNT_TYPE_FIELD;
        const disableCurrencySelect =
            isCurrencyField && (accountCurrencyOptions.length === 0 || bankDetails.length === 0);
        const disableAccountTypeSelect =
            isAccountTypeField
            && (
                (hasAccountCurrencyFieldOnForm && !currencyKey)
                || accountTypeOptions.length === 0
                || bankDetails.length === 0
            );
        const disableBankSelect =
            (field === YandexDocumentSelectField.ya_bank_name
                || field === YandexDocumentSelectField.ya_bic)
            && bankDetails.length === 0;
        const onOptionChange =
            field === YandexDocumentSelectField.ya_bank_name
                ? (o: unknown) => {
                    bankChainUiLeadRef.current = "bank";
                    clearBankActionTriggered.current = !o;
                    setValue(o ? (o as OptionType) : undefined);
                }
                : field === YandexDocumentSelectField.ya_bic
                    ? (o: unknown) => {
                        bankChainUiLeadRef.current = "bic";
                        setValue(o ? (o as OptionType) : undefined);
                    }
                    : (o: unknown) => setValue(o ? (o as OptionType) : undefined);
        return <SearchableSelect
            optionValue={value}
            validate={required}
            triggerValidate={triggerValidate}
            options={options}
            disabled={isExternalProtected
                || disableCurrencySelect
                || disableAccountTypeSelect
                || disableBankSelect
                || ([YandexDocumentSelectField.ya_corr_account, YandexDocumentSelectField.ya_payment_account].includes(field as YandexDocumentSelectField) && options.length === 1)}
            isMulti={false}
            isCreatable={false}
            isClearable={true}
            onOptionChange={onOptionChange}
        />;
    };

    return <div className={styles.componentContainer}>
        <div className={styles.titleContainer}>
            <div className={styles.title}>{label}</div>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {(!component.fieldsList || (Array.isArray(component.fieldsList) && component.fieldsList.length === 0)) &&
                <div>
                    {t("fieldListEmpty")}
                </div>}
            {sortFieldList(component.fieldsList).map((field: string) => {
                const readonlyBypassInnReg = !isExternalUser && isInnOrRegNumLegalYandexField(field);
                return <FormLabel
                    title={t(`legalYandexFields.${field as LegalYandexWordFields}`)}
                    disabled={isExternalProtected || (readonly && !isShowSelect(field) && !readonlyBypassInnReg)}
                    key={field}
                    required={required}>
                    {isShowSelect(field) ? renderSelect(field) :
                        <>
                            {!isDateField(field) && <DynamicInput size="m"
                                                                  textareaEdge={70}
                                                                  name={field + componentSuffix}
                                                                  containerClassName={styles.input}
                                                                  validate={required}
                                                                  validateFunc={validateFunction}
                                                                  triggerValidate={triggerValidate}
                                                                  disabled={readonly && !readonlyBypassInnReg}
                                                                  value={record[field] ? record[field] as string : ""}
                                                                  onChange={(e: any) => {
                                                                      if (field === LegalYandexWordFields.ya_inn) {
                                                                          setInn(e.target.value ? e.target.value : "");
                                                                      }
                                                                      if (field === LegalYandexWordFields.ya_reg_num) {
                                                                          setRegNum(e.target.value ? e.target.value : "");
                                                                      }
                                                                      onFieldChange(field, e.target.value ? e.target.value : undefined);
                                                                  }}
                                                                  view="outline"/>}
                            {isDateField(field) && <DatetimePicker
                                validate={required}
                                className={styles.input}
                                triggerValidate={triggerValidate}
                                disabled={readonly}
                                initialValue={record[field] ? record[field] as string : null}
                                dateFormat="DD.MM.YYYY"
                                onUpdate={(date) => onFieldChange(field, date ? date : undefined)}
                                withTime={false}
                            />}
                        </>
                    }
                </FormLabel>;
            })}
        </div>
    </div>;
});
